// Meat header. One sticky cream row: logo · nav links · deliver-to store
// picker · round search / wishlist / account / cart buttons; the search
// button drops a search panel below the row.
// Phones collapse to: burger · logo · cart, a full-width search row, the
// deliver-to chip and a bottom tab bar.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Badge, Grid, Dropdown } from 'antd';
import {
  AppstoreOutlined,
  DownOutlined,
  HeartOutlined,
  HomeOutlined,
  LogoutOutlined,
  ReadOutlined,
  SearchOutlined,
  ShoppingOutlined,
  UserOutlined,
  // UserSwitchOutlined,
} from '@ant-design/icons';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@core/hooks/useAuth';
// import { useAccountSelectionStore } from '@core/store/accountSelectionStore';
import { useActiveCart } from '@core/hooks/useCart';
import { useCategories } from '@core/hooks/useCatalog';
import { useLocations } from '@core/hooks/useLocations';
import { useWishlistStore } from '@core/store/wishlistStore';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useUiStore } from '@core/store/uiStore';
import { useRuntimeBrand } from '@core/hooks/useRuntimeBrand';
import { useProfile } from '@core/hooks/useAccount';
import { getAvatarStyles } from '@core/utils/avatar';
import CartDrawer from '@components/base/CartDrawer';
import CheckoutModal from '@components/base/CheckoutModal';
import {
  CHECKOUT_MODAL_REDIRECT,
  loadCheckoutModule,
  openCheckoutModal,
} from '@components/base/CheckoutModal/checkoutModalEvents.js';
import MobileNav from '@components/base/MobileNav';
import StorefrontAuthModal from '@components/base/StorefrontAuthModal';
import ImageWithFallback from '@components/base/ImageWithFallback';
import { useIndustry } from '@/app/providers/industry-context.js';
import { useAppLanguage } from '@/app/providers/useAppLanguage.js';
import { useCopy } from '@/app/providers/useCopy';

// Type pair for the meat skin; injected once, meat only.
const MEAT_FONTS_ID = 'meat-skin-fonts';
const MEAT_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Playfair+Display:wght@500;600;700&display=swap';

function useMeatFonts() {
  useEffect(() => {
    if (document.getElementById(MEAT_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = MEAT_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = MEAT_FONTS_HREF;
    document.head.appendChild(link);
  }, []);
}

const CATEGORY_MENU_LIMIT = 10;

const fallbackNav = [
  { label: 'Shop', to: '/catalog' },
  { label: 'Categories', to: '/catalog?view=categories' },
  { label: 'My Orders', to: '/orders' },
];

// Solid red map pin with a white dot (deliver-to store picker).
function LocationPin() {
  return (
    <svg className="mx-location__pin" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
      />
      <circle cx="12" cy="9" r="2.6" fill="#fff" />
    </svg>
  );
}

function BrandMark({ logoUrl, appName, showName = false }) {
  return (
    <Link to="/" className="mx-brand" aria-label={appName || 'Home'}>
      {logoUrl ? (
        <ImageWithFallback
          src={logoUrl}
          alt=""
          className="mx-brand__logo"
          aspectRatio="1 / 1"
          radius="0.5rem"
          loading="eager"
        />
      ) : (
        <span className="mx-brand__logo mx-brand__logo--mark" aria-hidden>
          <ShoppingOutlined />
        </span>
      )}
      {showName && appName ? <span className="mx-brand__name">{appName}</span> : null}
    </Link>
  );
}

export default function HeaderMeat() {
  const t = useCopy();
  useMeatFonts();
  useAppLanguage();
  const { config } = useIndustry();
  const { logoUrl: runtimeLogoUrl, brandName: runtimeBrandName } = useRuntimeBrand();
  const { customer, isAuthenticated, logout } = useAuth();
  const { lineCount } = useActiveCart();
  const wishlistCount = useWishlistStore((s) => s.items.length);
  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const openStoreSelector = useUiStore((s) => s.openStoreSelector);
  // const openAccountSelector = useAccountSelectionStore((s) => s.openAccountSelector);
  const categoriesQuery = useCategories({ limit: 100 });
  const profileQuery = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search } = location;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const headerRef = useRef(null);
  const searchInputRef = useRef(null);
  // True once the page has scrolled: the sticky header gains a shadow.
  const [isStuck, setIsStuck] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [authRedirect, setAuthRedirect] = useState(null);
  const [searchText, setSearchText] = useState('');

  const isCatalogPage = pathname === '/catalog' || pathname.startsWith('/catalog');
  const isSearchAllowedPage =
    pathname === '/' || isCatalogPage || pathname.startsWith('/categories');

  const appName = runtimeBrandName || config?.copy?.appName || config?.name || '';
  const logoUrl = runtimeLogoUrl || config?.logoUrl || '';
  const displayName =
    profileQuery.data?.first_name ||
    customer?.first_name ||
    customer?.email?.split('@')[0] ||
    t('account', 'Account');

  const selectedLocation = locations.find((loc) => loc.id === locationId);
  const selectedLocationLabel =
    selectedLocation?.name ||
    [selectedLocation?.city, selectedLocation?.state].filter(Boolean).join(', ') ||
    '';

  const primaryNav = config?.nav?.primary?.length ? config.nav.primary : fallbackNav;

  const rootCategories = useMemo(() => {
    const all = categoriesQuery.data ?? [];
    const roots = all.filter((category) => !category.parentId);
    return (roots.length ? roots : all).map((category) => ({
      id: category.id,
      name: category.name,
      imageUrl: category.imageUrl,
      to: `/catalog?category=${category.id}`,
    }));
  }, [categoriesQuery.data]);

  const mobileMenuItems = useMemo(
    () => [
      { to: '/', label: t('home', 'Home') },
      ...primaryNav.map((item) => ({ to: item.to, label: t(item.label, item.label) })),
      ...rootCategories.slice(0, CATEGORY_MENU_LIMIT).map((c) => ({ to: c.to, label: c.name })),
    ],
    [primaryNav, rootCategories, t],
  );

  // Publish the header height so sticky page chrome (catalog sidebar, cart
  // summary) can sit just below it.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty(
        '--meat-header-height',
        `${entry.target.offsetHeight}px`,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setIsStuck(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  // Keep the box in step with ?q= when the catalog drives the search, and
  // empty it on leaving the catalog (e.g. back to the home page).
  useEffect(() => {
    if (!isCatalogPage) {
      clearTimeout(searchTimeoutRef.current);
      setSearchText('');
      return;
    }
    const q = new URLSearchParams(search).get('q') ?? '';
    setSearchText((current) => (current.trim() === q.trim() ? current : q));
  }, [isCatalogPage, search]);

  useEffect(() => {
    if (!location.state?.openCartDrawer) return;
    setCartOpen(true);
    navigate(`${pathname}${search}`, { replace: true, state: null });
  }, [location.state, navigate, pathname, search]);

  useEffect(() => {
    const handleOpenAuthModal = (e) => {
      const { mode = 'login', redirect = null } = e.detail || {};
      setAuthMode(mode);
      if (redirect) setAuthRedirect(redirect);
    };
    window.addEventListener('open-auth-modal', handleOpenAuthModal);
    return () => window.removeEventListener('open-auth-modal', handleOpenAuthModal);
  }, []);

  // focusSearch: on phones the catalog hides the app bar search, so hand focus
  // to the catalog's own search box and the shopper keeps typing there.
  const runSearch = (value, { focusSearch = false } = {}) => {
    if (!isSearchAllowedPage) return;
    const q = value.trim();
    navigate(q ? `/catalog?q=${encodeURIComponent(q)}` : '/catalog', {
      state: focusSearch ? { focusSearch: true } : undefined,
    });
  };

  // Live search from any page (like grocery): results open 300ms after the
  // shopper stops typing.
  const onSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    clearTimeout(searchTimeoutRef.current);
    if (!value.trim() && !isCatalogPage) return;
    searchTimeoutRef.current = setTimeout(
      () => runSearch(value, { focusSearch: isMobile && !isCatalogPage }),
      300,
    );
  };

  const onSearchSubmit = (event) => {
    event.preventDefault();
    clearTimeout(searchTimeoutRef.current);
    runSearch(searchText);
  };

  const requireAuth = (redirect) => {
    if (isAuthenticated) {
      navigate(redirect);
      return;
    }
    setAuthRedirect(redirect);
    setAuthMode('login');
  };

  const accountItems = [
    { key: 'email', label: customer?.email ?? displayName ?? '', disabled: true },
    { type: 'divider' },
    { key: 'profile', icon: <UserOutlined />, label: t('profile', 'Profile') },
    // { key: 'orders', icon: <ReadOutlined />, label: t('myOrders', 'My Orders') },
    // Switch Account is hidden for the meat skin (restore with the import,
    // openAccountSelector and the handler below).
    // {
    //   key: 'switch_account',
    //   icon: <UserSwitchOutlined />,
    //   label: t('switchAccount', 'Switch Account'),
    // },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('signOut', 'Sign Out') },
  ];

  const onAccountClick = ({ key }) => {
    if (key === 'profile') navigate('/account');
    if (key === 'orders') navigate('/orders');
    // if (key === 'switch_account') openAccountSelector();
    if (key === 'logout') {
      logout();
      navigate('/');
    }
  };

  const closeCart = useCallback(() => setCartOpen(false), []);
  // Checkout opens in the shared modal over the page; after a login it
  // reopens there instead of routing to /checkout.
  const goToCheckout = () => {
    if (isAuthenticated) {
      openCheckoutModal();
      return;
    }
    setAuthRedirect(CHECKOUT_MODAL_REDIRECT);
    setAuthMode('login');
  };

  // Fetch the checkout code while the cart is open so the modal opens fast.
  useEffect(() => {
    if (cartOpen) loadCheckoutModule();
  }, [cartOpen]);

  const mobileTabs = [
    { key: 'home', to: '/', label: t('home', 'Home'), icon: <HomeOutlined /> },
    {
      key: 'category',
      to: '/catalog?view=categories',
      label: t('category', 'Category'),
      icon: <AppstoreOutlined />,
    },
    { key: 'shop', to: '/catalog', label: t('shop', 'Shop'), icon: <ShoppingOutlined /> },
    { key: 'orders', to: '/orders', label: t('orders', 'Orders'), icon: <ReadOutlined /> },
  ];

  const isTabActive = (tab) => {
    const isCategoryView = new URLSearchParams(search).get('view') === 'categories';
    if (tab.key === 'home') return pathname === '/';
    if (tab.key === 'category') return isCatalogPage && isCategoryView;
    if (tab.key === 'shop') return pathname === '/catalog' && !isCategoryView;
    return pathname.startsWith(tab.to);
  };

  const isNavActive = (to) => {
    const [toPath, toQuery = ''] = to.split('?');
    if (toPath !== pathname) return false;
    return toQuery ? search.includes(toQuery) : !search.includes('view=');
  };

  const searchForm = isSearchAllowedPage ? (
    <form className="mx-search" role="search" onSubmit={onSearchSubmit}>
      <SearchOutlined className="mx-search__icon" aria-hidden />
      <input
        ref={searchInputRef}
        type="search"
        value={searchText}
        onChange={onSearchChange}
        onInput={onSearchChange}
        placeholder={t('searchPlaceholder', 'Search for products...')}
        aria-label={t('searchProducts', 'Search products')}
      />
      {/* Phones submit from the keyboard; desktop gets the Search button. */}
      {isMobile ? null : (
        <button type="submit" className="mx-search__submit" aria-label={t('search', 'Search')}>
          {t('search', 'Search')}
        </button>
      )}
    </form>
  ) : (
    <div className="mx-search mx-search--placeholder" aria-hidden />
  );

  // Account: icon over a label on desktop, a round icon button on phones
  // (menu when signed in, sign-in modal otherwise).
  // Signed in: a coloured avatar with the shopper's initial (and their name
  // on desktop) instead of the generic person icon, so the state is obvious.
  const accountIcon = isAuthenticated ? (
    <Avatar
      size={isMobile ? 30 : 24}
      className="mx-account-avatar"
      style={getAvatarStyles(displayName)}
    >
      {displayName[0]?.toUpperCase()}
    </Avatar>
  ) : (
    <UserOutlined />
  );
  const accountButtonProps = isMobile
    ? { className: 'mx-round mx-round--account', children: accountIcon }
    : {
        className: `mx-action ${isAuthenticated ? 'is-signed-in' : ''}`,
        children: (
          <>
            {accountIcon}
            <span>{isAuthenticated ? displayName : t('signIn', 'Sign in')}</span>
          </>
        ),
      };
  const accountControl = isAuthenticated ? (
    <Dropdown menu={{ items: accountItems, onClick: onAccountClick }} trigger={['click']}>
      <button
        type="button"
        className={accountButtonProps.className}
        aria-label={`${t('account', 'Account')}: ${displayName}`}
        title={displayName}
      >
        {accountButtonProps.children}
      </button>
    </Dropdown>
  ) : (
    <button
      type="button"
      className={accountButtonProps.className}
      aria-label={t('signIn', 'Log in')}
      title={t('signIn', 'Log in')}
      onClick={() => setAuthMode('choice')}
    >
      {accountButtonProps.children}
    </button>
  );

  const navLinks = (
    <ul className="mx-nav">
      <li>
        <NavLink to="/" end className={() => (pathname === '/' ? 'is-active' : '')}>
          {t('home', 'Home')}
        </NavLink>
      </li>
      {primaryNav.map((item) => (
        <li key={item.to}>
          <Link
            to={item.to}
            className={isNavActive(item.to) ? 'is-active' : ''}
            onClick={(e) => {
              if (item.to === '/orders' && !isAuthenticated) {
                e.preventDefault();
                requireAuth('/orders');
              }
            }}
          >
            {t(item.label, item.label)}
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <header ref={headerRef} className={`meat-header mx-header ${isStuck ? 'is-stuck' : ''}`}>
        {isMobile ? (
          // Phone app bar on the light primary tint: logo and the store
          // picker on the left, wishlist and profile
          // (account menu) on the right, then a white search pill. The cart
          // lives in the floating cart bar.
          <div className="mx-apphead">
            <div className="mx-apphead__row">
              <div className="mx-apphead__start">
                <BrandMark logoUrl={logoUrl} appName={appName} />
                <button
                  type="button"
                  className="mx-apphead__location"
                  onClick={openStoreSelector}
                  aria-label={`${t('changeStore', 'Change store')}: ${selectedLocationLabel}`}
                >
                  <strong>
                    <LocationPin />
                    <span>{selectedLocationLabel}</span>
                    <DownOutlined className="mx-apphead__caret" />
                  </strong>
                </button>
              </div>
              <div className="mx-apphead__end">
                <Link
                  to="/wishlist"
                  state={{ refreshFavoritesAt: Date.now() }}
                  className="mx-round mx-round--wishlist"
                  aria-label={t('wishlist', 'Wishlist')}
                >
                  <Badge count={wishlistCount} size="small">
                    <HeartOutlined />
                  </Badge>
                </Link>
                {accountControl}
              </div>
            </div>
            {/* The catalog pages (products and categories) have their own
                search in the page toolbar, so the app bar drops it there. */}
            {isCatalogPage || pathname.startsWith('/categories') ? null : searchForm}
          </div>
        ) : (
          <>
            <div className="mx-wrap mx-topbar">
              <BrandMark logoUrl={logoUrl} appName={appName} showName />

              <button
                type="button"
                className="mx-topbar__location"
                onClick={openStoreSelector}
                aria-label={`${t('changeStore', 'Change store')}: ${selectedLocationLabel}`}
              >
                <LocationPin />
                <strong>{selectedLocationLabel}</strong>
                <DownOutlined className="mx-topbar__caret" />
              </button>

              <nav className="mx-topbar__nav" aria-label={t('mainNavigation', 'Main navigation')}>
                {navLinks}
              </nav>

              <div className="mx-topbar__search">{searchForm}</div>

              <div className="mx-topbar__actions">
                <Link
                  to="/wishlist"
                  state={{ refreshFavoritesAt: Date.now() }}
                  className="mx-action"
                >
                  <Badge count={wishlistCount} size="small">
                    <HeartOutlined />
                  </Badge>
                  <span>{t('wishlist', 'Wishlist')}</span>
                </Link>

                {accountControl}

                <button
                  type="button"
                  className="mx-cart-btn"
                  aria-label={t('openCart', 'Open cart')}
                  onClick={() => setCartOpen(true)}
                >
                  <ShoppingOutlined />
                  <span>{t('cart', 'Cart')}</span>
                  <span className="mx-cart-btn__count">{lineCount}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </header>

      <MobileNav
        open={navOpen}
        onClose={() => setNavOpen(false)}
        items={mobileMenuItems}
        className="mx-mobile-nav"
        title={<BrandMark logoUrl={logoUrl} appName={appName} />}
      />

      {isMobile ? (
        <nav className="meat-mobile-tabs mx-tabbar" aria-label="Mobile navigation">
          {mobileTabs.map((tab) => (
            <Link
              key={tab.key}
              to={tab.to}
              onClick={(e) => {
                if (tab.to === '/orders' && !isAuthenticated) {
                  e.preventDefault();
                  requireAuth('/orders');
                }
              }}
              className={`mx-tabbar__tab ${isTabActive(tab) ? 'is-active' : ''}`}
            >
              <span className="mx-tabbar__icon">{tab.icon}</span>
              <span className="mx-tabbar__label">{tab.label}</span>
            </Link>
          ))}
          {/* No Account tab: the app bar's account button covers it. */}
        </nav>
      ) : null}

      <CartDrawer
        open={cartOpen}
        onClose={closeCart}
        onCheckout={goToCheckout}
        classPrefix="meat"
        editModalClassName="grocery-product-modal"
      />
      <CheckoutModal className="meat-checkout-modal" />

      <StorefrontAuthModal
        open={Boolean(authMode)}
        mode={authMode ?? 'choice'}
        onCancel={() => {
          setAuthMode(null);
          setAuthRedirect(null);
        }}
        onAuthenticated={() => {
          if (authRedirect === CHECKOUT_MODAL_REDIRECT) openCheckoutModal();
          else if (authRedirect) navigate(authRedirect);
          setAuthRedirect(null);
        }}
      />
    </>
  );
}
